import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuthStore } from "@/store/authStore";
import { useThemeStore } from "@/store/themeStore";
import { useAdminStore } from "@/store/adminStore";
import { Loader2 } from "lucide-react";
import { TranslateProvider } from "@/providers/TranslateProvider";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as SonnerToaster } from "@/components/ui/sonner";
import CookieConsentBanner from "@/components/CookieConsentBanner";

/**
 * Lazy-load a route component with automatic recovery from stale chunk
 * references (the classic "Unexpected token '<'" / "Loading chunk failed"
 * crash that happens after a redeploy when the user's main.js still points
 * at chunk hashes that no longer exist on the server).
 *
 * On the first load failure we set a one-shot flag in sessionStorage and
 * hard-reload the page so the browser fetches the new main.js + chunk
 * manifest. If it fails again we surface the original error.
 */
const lazyWithRetry = (importer) =>
  lazy(async () => {
    try {
      return await importer();
    } catch (err) {
      const flag = "__chunk_reload_attempted__";
      const already = sessionStorage.getItem(flag);
      if (!already) {
        sessionStorage.setItem(flag, "1");
        window.location.reload();
        // Return a never-resolving promise so React doesn't try to render anything.
        return new Promise(() => {});
      }
      throw err;
    }
  });

// ─── Aggressive Global Scroll Fix ───────────────────────────────
function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    // Successful navigation — clear the chunk-reload guard so a fresh failure
    // can retry once.
    sessionStorage.removeItem("__chunk_reload_attempted__");

    if (hash) {
      setTimeout(() => {
        const id = hash.replace('#', '');
        const element = document.getElementById(id);
        if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
      return;
    }

    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    const rootWrapper = document.getElementById('root');
    if (rootWrapper) rootWrapper.scrollTop = 0;

    const appWrapper = document.querySelector('.App');
    if (appWrapper) appWrapper.scrollTop = 0;
  }, [pathname, hash]);

  return null;
}

// ─── Marketing / Public Pages (lazy) ────────────────────────────
const LandingPage = lazyWithRetry(() => import('./pages/LandingPage'));
const FeaturesPage = lazyWithRetry(() => import('./pages/FeaturesPage'));
const ComparePage = lazyWithRetry(() => import('./pages/ComparePage'));
const PricingPage = lazyWithRetry(() => import('./pages/PricingPage'));
const EnterprisePage = lazyWithRetry(() => import('./pages/EnterprisePage'));
const SolutionPage = lazyWithRetry(() => import('./pages/SolutionPage'));
const IndustryPage = lazyWithRetry(() => import('./pages/IndustryPage'));
const ContactPage = lazyWithRetry(() => import('./pages/ContactUsPage'));
const IntegrationPage = lazyWithRetry(() => import('@/pages/IntegrationPage'));
const APIDocsPage = lazyWithRetry(() => import('@/pages/APIDocsPage'));
const BlogPage = lazyWithRetry(() => import('@/pages/BlogPage'));
const DemoPage = lazyWithRetry(() => import('@/pages/DemoPage'));
const BlogPostPage = lazyWithRetry(() => import('./pages/BlogPostPage'));
const AboutPage = lazyWithRetry(() => import('./pages/AboutPage'));

// ─── Auth Pages (lazy) ──────────────────────────────────────────
const LoginPage = lazyWithRetry(() => import('@/pages/LoginPage'));
const RegisterPage = lazyWithRetry(() => import('@/pages/RegisterPage'));
const ForgotPasswordPage = lazyWithRetry(() => import('@/pages/ForgotPasswordPage'));
const ResetPasswordPage = lazyWithRetry(() => import('@/pages/ResetPasswordPage'));
const ChoosePlanPage = lazyWithRetry(() => import('@/pages/ChoosePlanPage'));
const CheckoutPage = lazyWithRetry(() => import('@/pages/CheckoutPage'));
const AccountLockedPage = lazyWithRetry(() => import('@/pages/AccountLockedPage'));

// ─── App Pages (lazy) ───────────────────────────────────────────
const DashboardPage = lazyWithRetry(() => import('@/pages/DashboardPage'));
const ChatbotsPage = lazyWithRetry(() => import('@/pages/ChatbotsPage'));
const CreateChatbotPage = lazyWithRetry(() => import('@/pages/CreateChatbotPage'));
const ChatbotDetailPage = lazyWithRetry(() => import('@/pages/ChatbotDetailPage'));
const ConversationsPage = lazyWithRetry(() => import('@/pages/ConversationsPage'));
const KnowledgeBasesPage = lazyWithRetry(() => import('@/pages/KnowledgeBasesPage'));
const KnowledgeBaseDetailPage = lazyWithRetry(() => import('@/pages/KnowledgeBaseDetailPage'));
const AnalyticsPage = lazyWithRetry(() => import('@/pages/AnalyticsPage'));
const ConversationIntelligencePage = lazyWithRetry(() => import('@/pages/ConversationIntelligencePage'));
const AIInsightsPage = lazyWithRetry(() => import('@/pages/AIInsightsPage'));
const IntegrationsPage = lazyWithRetry(() => import('@/pages/IntegrationsPage'));
const APIIntegrationsPage = lazyWithRetry(() => import('@/pages/APIIntegrationsPage'));
const NewIntegrationPage = lazyWithRetry(() => import('@/pages/NewIntegrationPage'));
const EditIntegrationPage = lazyWithRetry(() => import('@/pages/EditIntegrationPage'));
const IntegrationDetailPage = lazyWithRetry(() => import('@/pages/IntegrationDetailPage'));
const FlowBuilderPage = lazyWithRetry(() => import('@/pages/FlowBuilderPage'));
const TemplatesPage = lazyWithRetry(() => import('@/pages/TemplatesPage'));
const SettingsPage = lazyWithRetry(() => import('@/pages/SettingsPage'));
const WidgetPage = lazyWithRetry(() => import('@/pages/WidgetPage'));
const PrivacyPolicyPage = lazyWithRetry(() => import('@/pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazyWithRetry(() => import('@/pages/TermsOfServicePage'));
const CookiePolicyPage = lazyWithRetry(() => import('@/pages/CookiePolicyPage'));
const PrivacySettingsPage = lazyWithRetry(() => import('@/pages/PrivacySettingsPage'));
const MobileIntegrationPage = lazyWithRetry(() => import('@/pages/MobileIntegrationPage'));
const MobileAnalyticsPage = lazyWithRetry(() => import('@/pages/MobileAnalyticsPage'));

// ─── Admin Pages (lazy) ─────────────────────────────────────────
const AdminLoginPage = lazyWithRetry(() => import('@/pages/admin/AdminLoginPage'));
const AdminForgotPasswordPage = lazyWithRetry(() => import('@/pages/admin/AdminForgotPasswordPage'));
const AdminResetPasswordPage = lazyWithRetry(() => import('@/pages/admin/AdminResetPasswordPage'));
const AdminDashboardPage = lazyWithRetry(() => import('@/pages/admin/AdminDashboardPage'));
const AdminUsersPage = lazyWithRetry(() => import('@/pages/admin/AdminUsersPage'));
const AdminAdminsPage = lazyWithRetry(() => import('@/pages/admin/AdminAdminsPage'));
const AdminLLMProvidersPage = lazyWithRetry(() => import('@/pages/admin/AdminLLMProvidersPage'));
const AdminDomainsPage = lazyWithRetry(() => import('@/pages/admin/AdminDomainsPage'));
const AdminSettingsPage = lazyWithRetry(() => import('@/pages/admin/AdminSettingsPage'));
const AdminUserLimitsPage = lazyWithRetry(() => import('@/pages/admin/AdminUserLimitsPage'));
const AdminChatbotsPage = lazyWithRetry(() => import('@/pages/admin/AdminChatbotsPage'));
const AdminAuditLogsPage = lazyWithRetry(() => import('@/pages/admin/AdminAuditLogsPage'));
const AdminEmailBuilderPage = lazyWithRetry(() => import('@/pages/admin/AdminEmailBuilderPage'));
const AdminCouponsPage = lazyWithRetry(() => import('@/pages/admin/AdminCouponsPage'));
const AdminPricingPage = lazyWithRetry(() => import('@/pages/admin/AdminPricingPage'));
const AdminAutomationSettingsPage = lazyWithRetry(() => import('@/pages/admin/AdminAutomationSettingsPage'));
const AdminBlogPage = lazyWithRetry(() => import('@/pages/admin/AdminBlogPage'));
const AdminForumPage = lazyWithRetry(() => import('@/pages/admin/AdminForumPage'));
const AdminManagementPage = lazyWithRetry(() => import('@/pages/admin/AdminManagementPage'));
const AdminDemoChatbotPage = lazyWithRetry(() => import('@/pages/admin/AdminDemoChatbotPage'));

// ─── Public Pages (lazy) ────────────────────────────────────────
const ShowcasePage = lazyWithRetry(() => import('@/pages/ShowcasePage'));

// ─── Agent Pages (lazy) ─────────────────────────────────────────
const AgentLoginPage = lazyWithRetry(() => import('@/pages/agent/AgentLoginPage'));
const AgentDashboardPage = lazyWithRetry(() => import('@/pages/agent/AgentDashboardPage'));
const AgentChatPage = lazyWithRetry(() => import('@/pages/agent/AgentChatPage'));
const AgentQueuePage = lazyWithRetry(() => import('@/pages/agent/AgentQueuePage'));
const AgentChatsPage = lazyWithRetry(() => import('@/pages/agent/AgentChatsPage'));
const AgentHistoryPage = lazyWithRetry(() => import('@/pages/agent/AgentHistoryPage'));
const AgentSettingsPage = lazyWithRetry(() => import('@/pages/agent/AgentSettingsPage'));

// ─── Feature Pages (lazy) ───────────────────────────────────────
const MyAgentsPage = lazyWithRetry(() => import('@/pages/MyAgentsPage'));
const EcommercePage = lazyWithRetry(() => import('@/pages/EcommercePage'));
const ProductCatalogPage = lazyWithRetry(() => import('@/pages/ProductCatalogPage'));
const HubSpotPipelinePage = lazyWithRetry(() => import('@/pages/HubSpotPipelinePage'));
const WooCommerceOrdersPage = lazyWithRetry(() => import('@/pages/WooCommerceOrdersPage'));
const MagentoOrdersPage = lazyWithRetry(() => import('@/pages/MagentoOrdersPage'));
const BigCommerceOrdersPage = lazyWithRetry(() => import('@/pages/BigCommerceOrdersPage'));
const OpenCartOrdersPage = lazyWithRetry(() => import('@/pages/OpenCartOrdersPage'));
const SDKDownloadPage = lazyWithRetry(() => import('@/pages/SDKDownloadPage'));
const ThirdPartyIntegrationsPage = lazyWithRetry(() => import('@/pages/ThirdPartyIntegrationsPage'));
const ForumPage = lazyWithRetry(() => import('@/pages/ForumPage'));
const ReferralsPage = lazyWithRetry(() => import('@/pages/ReferralsPage'));
const AutomationPage = lazyWithRetry(() => import('@/pages/AutomationPage'));
const AutomationEditorPage = lazyWithRetry(() => import('@/pages/AutomationEditorPage'));
const VisualWorkflowEditorPage = lazyWithRetry(() => import('@/pages/VisualWorkflowEditorPage'));
const AIOptimizerPage = lazyWithRetry(() => import('@/pages/AIOptimizerPage'));
const CustomerInsightsPage = lazyWithRetry(() => import('@/pages/CustomerInsightsPage'));
const EmailSequencesPage = lazyWithRetry(() => import('@/pages/EmailSequencesPage'));
const EmailSequenceEditorPage = lazyWithRetry(() => import('@/pages/EmailSequenceEditorPage'));
const PartnerProgramPage = lazyWithRetry(() => import('@/pages/PartnerProgramPage'));
const PartnerDashboardPage = lazyWithRetry(() => import('@/pages/PartnerDashboardPage'));
const AdminPartnersPage = lazyWithRetry(() => import('@/pages/admin/AdminPartnersPage'));
const AgencyDirectoryPage = lazyWithRetry(() => import('@/pages/AgencyDirectoryPage'));
const AgencyProfilePage = lazyWithRetry(() => import('@/pages/AgencyProfilePage'));
const AgencyProfileEditorPage = lazyWithRetry(() => import('@/pages/AgencyProfileEditorPage'));
const AdminDirectoryPage = lazyWithRetry(() => import('@/pages/admin/AdminDirectoryPage'));
const AdminSalesInboxPage = lazyWithRetry(() => import('@/pages/admin/AdminSalesInboxPage'));
const VSPage = lazyWithRetry(() => import('@/pages/VSPage'));


// ─── Shared Loading Fallback ────────────────────────────────────
const RouteLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#061016]">
    <Loader2 className="h-8 w-8 animate-spin text-[#2596BE]" data-testid="route-loader" />
  </div>
);

// ─── Route Guards ────────────────────────────────────────────
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isInitialized } = useAuthStore();
  if (!isInitialized) return <RouteLoader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, isInitialized } = useAuthStore();
  if (!isInitialized) return <RouteLoader />;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return children;
};

const AdminProtectedRoute = ({ children }) => {
  const { adminToken, admin } = useAdminStore();
  const hasAdminToken = adminToken || localStorage.getItem('adminToken');
  if (!hasAdminToken) return <Navigate to="/" replace />;
  // adminToken present but admin profile not yet fetched — show nothing until the
  // AdminLayout's loader resolves.
  return children;
};

function App() {
  const { isInitialized, initialize } = useAuthStore();
  const { fetchAdmin, adminToken } = useAdminStore();

  useEffect(() => {
    initialize();
    if (localStorage.getItem('adminToken') && !adminToken) fetchAdmin();
  }, []);

  useEffect(() => {
    const unsub = useThemeStore.persist.onFinishHydration(() => {
      useThemeStore.getState().initTheme();
    });
    if (useThemeStore.persist.hasHydrated()) {
      useThemeStore.getState().initTheme();
    }
    return unsub;
  }, []);

  return (
    <TranslateProvider>
      <div className="App">
        <BrowserRouter>
          <ScrollToTop />

          <Suspense fallback={<RouteLoader />}>
            <Routes>
              {/* ══════════════════════════════════════
                  MARKETING PAGES
              ══════════════════════════════════════ */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/features" element={<FeaturesPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/enterprise" element={<EnterprisePage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/integration" element={<IntegrationPage />} />
              <Route path="/docs" element={<APIDocsPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/demo" element={<DemoPage />} />
              <Route path="/about" element={<AboutPage />} />

              <Route path="/compare" element={<ComparePage />} />
              <Route path="/compare/:competitor" element={<ComparePage />} />
              <Route path="/vs/tidio" element={<VSPage competitorKey="tidio" />} />
              <Route path="/vs/intercom" element={<VSPage competitorKey="intercom" />} />
              <Route path="/vs/crisp" element={<VSPage competitorKey="crisp" />} />
              <Route path="/vs/drift" element={<VSPage competitorKey="drift" />} />
              <Route path="/vs/freshdesk" element={<VSPage competitorKey="freshdesk" />} />

              <Route path="/solutions/customer-support" element={<SolutionPage />} />
              <Route path="/solutions/lead-generation" element={<SolutionPage />} />
              <Route path="/solutions/ecommerce" element={<SolutionPage />} />
              <Route path="/solutions/appointment-booking" element={<SolutionPage />} />
              <Route path="/solutions/conversational-ai" element={<SolutionPage />} />
              <Route path="/solutions/:slug" element={<SolutionPage />} />

              <Route path="/industry/hotels" element={<IndustryPage />} />
              <Route path="/industry/healthcare" element={<IndustryPage />} />
              <Route path="/industry/ecommerce" element={<IndustryPage />} />
              <Route path="/industry/real-estate" element={<IndustryPage />} />
              <Route path="/industry/legal" element={<IndustryPage />} />
              <Route path="/industry/restaurants" element={<IndustryPage />} />
              <Route path="/industry/:slug" element={<IndustryPage />} />

              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/terms" element={<TermsOfServicePage />} />
              <Route path="/cookie-policy" element={<CookiePolicyPage />} />

              <Route path="/widget/:chatbotId" element={<WidgetPage />} />
              <Route path="/showcase" element={<ShowcasePage />} />
              <Route path="/sdk" element={<SDKDownloadPage />} />

              {/* ══════════════════════════════════════
                  AUTH PAGES
              ══════════════════════════════════════ */}
              <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
              <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />

              {/* ══════════════════════════════════════
                  APP — PROTECTED
              ══════════════════════════════════════ */}
              <Route path="/choose-plan" element={<ProtectedRoute><ChoosePlanPage /></ProtectedRoute>} />
              <Route path="/checkout" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
              <Route path="/account-locked" element={<ProtectedRoute><AccountLockedPage /></ProtectedRoute>} />
              <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
              <Route path="/chatbots" element={<ProtectedRoute><ChatbotsPage /></ProtectedRoute>} />
              <Route path="/chatbots/create" element={<ProtectedRoute><CreateChatbotPage /></ProtectedRoute>} />
              <Route path="/chatbots/:id" element={<ProtectedRoute><ChatbotDetailPage /></ProtectedRoute>} />
              <Route path="/chatbots/:id/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
              <Route path="/conversations" element={<ProtectedRoute><ConversationsPage /></ProtectedRoute>} />
              <Route path="/knowledge-bases" element={<ProtectedRoute><KnowledgeBasesPage /></ProtectedRoute>} />
              <Route path="/knowledge-bases/:id" element={<ProtectedRoute><KnowledgeBaseDetailPage /></ProtectedRoute>} />
              <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
              <Route path="/analytics/intelligence" element={<ProtectedRoute><ConversationIntelligencePage /></ProtectedRoute>} />
              <Route path="/ai-insights" element={<ProtectedRoute><AIInsightsPage /></ProtectedRoute>} />
              <Route path="/integrations" element={<ProtectedRoute><IntegrationsPage /></ProtectedRoute>} />
              <Route path="/api-integrations" element={<ProtectedRoute><APIIntegrationsPage /></ProtectedRoute>} />
              <Route path="/api-integrations/new" element={<ProtectedRoute><NewIntegrationPage /></ProtectedRoute>} />
              <Route path="/api-integrations/:id" element={<ProtectedRoute><IntegrationDetailPage /></ProtectedRoute>} />
              <Route path="/api-integrations/:id/edit" element={<ProtectedRoute><EditIntegrationPage /></ProtectedRoute>} />
              <Route path="/flow-builder/:flowId" element={<ProtectedRoute><FlowBuilderPage /></ProtectedRoute>} />
              <Route path="/templates" element={<ProtectedRoute><TemplatesPage /></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
              <Route path="/settings/billing" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
              <Route path="/settings/privacy" element={<ProtectedRoute><PrivacySettingsPage /></ProtectedRoute>} />
              <Route path="/my-agents" element={<ProtectedRoute><MyAgentsPage /></ProtectedRoute>} />
              <Route path="/ecommerce" element={<ProtectedRoute><EcommercePage /></ProtectedRoute>} />
              <Route path="/product-catalog" element={<ProtectedRoute><ProductCatalogPage /></ProtectedRoute>} />
              <Route path="/hubspot-pipeline" element={<ProtectedRoute><HubSpotPipelinePage /></ProtectedRoute>} />
              <Route path="/woocommerce-orders" element={<ProtectedRoute><WooCommerceOrdersPage /></ProtectedRoute>} />
              <Route path="/magento-orders" element={<ProtectedRoute><MagentoOrdersPage /></ProtectedRoute>} />
              <Route path="/bigcommerce-orders" element={<ProtectedRoute><BigCommerceOrdersPage /></ProtectedRoute>} />
              <Route path="/opencart-orders" element={<ProtectedRoute><OpenCartOrdersPage /></ProtectedRoute>} />
              <Route path="/dashboard/sdk" element={<ProtectedRoute><SDKDownloadPage /></ProtectedRoute>} />
              <Route path="/third-party-integrations" element={<ProtectedRoute><ThirdPartyIntegrationsPage /></ProtectedRoute>} />
              <Route path="/mobile-integration" element={<ProtectedRoute><MobileIntegrationPage /></ProtectedRoute>} />
              <Route path="/mobile-analytics" element={<ProtectedRoute><MobileAnalyticsPage /></ProtectedRoute>} />
              <Route path="/referrals" element={<ProtectedRoute><ReferralsPage /></ProtectedRoute>} />
              <Route path="/forum" element={<ForumPage />} />
              <Route path="/mgmt-a8f4c2d9e1b7/automation" element={<ProtectedRoute><AutomationPage /></ProtectedRoute>} />
              <Route path="/mgmt-a8f4c2d9e1b7/automation/:workflowId" element={<ProtectedRoute><AutomationEditorPage /></ProtectedRoute>} />
              <Route path="/mgmt-a8f4c2d9e1b7/automation/visual/:workflowId" element={<ProtectedRoute><VisualWorkflowEditorPage /></ProtectedRoute>} />
              <Route path="/mgmt-a8f4c2d9e1b7/email-sequences" element={<AdminProtectedRoute><EmailSequencesPage /></AdminProtectedRoute>} />
              <Route path="/mgmt-a8f4c2d9e1b7/email-sequences/:sequenceId" element={<AdminProtectedRoute><EmailSequenceEditorPage /></AdminProtectedRoute>} />
              <Route path="/ai-optimizer" element={<ProtectedRoute><AIOptimizerPage /></ProtectedRoute>} />
              <Route path="/customer-insights" element={<ProtectedRoute><CustomerInsightsPage /></ProtectedRoute>} />
              <Route path="/email-sequences" element={<ProtectedRoute><EmailSequencesPage /></ProtectedRoute>} />
              <Route path="/email-sequences/:sequenceId" element={<ProtectedRoute><EmailSequenceEditorPage /></ProtectedRoute>} />
              <Route path="/partner-program" element={<PartnerProgramPage />} />
              <Route path="/dashboard/partner" element={<ProtectedRoute><PartnerDashboardPage /></ProtectedRoute>} />
              <Route path="/agency-directory" element={<AgencyDirectoryPage />} />
              <Route path="/agency-directory/:slug" element={<AgencyProfilePage />} />
              <Route path="/dashboard/agency-profile" element={<ProtectedRoute><AgencyProfileEditorPage /></ProtectedRoute>} />

              {/* ══════════════════════════════════════
                  ADMIN
              ══════════════════════════════════════ */}
              <Route path="/mgmt-a8f4c2d9e1b7/login" element={<AdminLoginPage />} />
              <Route path="/mgmt-a8f4c2d9e1b7/forgot-password" element={<AdminForgotPasswordPage />} />
              <Route path="/mgmt-a8f4c2d9e1b7/reset-password" element={<AdminResetPasswordPage />} />
              <Route path="/mgmt-a8f4c2d9e1b7" element={<AdminProtectedRoute><AdminDashboardPage /></AdminProtectedRoute>} />
              <Route path="/mgmt-a8f4c2d9e1b7/users" element={<AdminProtectedRoute><AdminUsersPage /></AdminProtectedRoute>} />
              <Route path="/mgmt-a8f4c2d9e1b7/user-limits" element={<AdminProtectedRoute><AdminUserLimitsPage /></AdminProtectedRoute>} />
              <Route path="/mgmt-a8f4c2d9e1b7/chatbots" element={<AdminProtectedRoute><AdminChatbotsPage /></AdminProtectedRoute>} />
              <Route path="/mgmt-a8f4c2d9e1b7/admins" element={<AdminProtectedRoute><AdminAdminsPage /></AdminProtectedRoute>} />
              <Route path="/mgmt-a8f4c2d9e1b7/llm-providers" element={<AdminProtectedRoute><AdminLLMProvidersPage /></AdminProtectedRoute>} />
              <Route path="/mgmt-a8f4c2d9e1b7/domains" element={<AdminProtectedRoute><AdminDomainsPage /></AdminProtectedRoute>} />
              <Route path="/mgmt-a8f4c2d9e1b7/settings" element={<AdminProtectedRoute><AdminSettingsPage /></AdminProtectedRoute>} />
              <Route path="/mgmt-a8f4c2d9e1b7/audit" element={<AdminProtectedRoute><AdminAuditLogsPage /></AdminProtectedRoute>} />
              <Route path="/mgmt-a8f4c2d9e1b7/email-builder" element={<AdminProtectedRoute><AdminEmailBuilderPage /></AdminProtectedRoute>} />
              <Route path="/mgmt-a8f4c2d9e1b7/coupons" element={<AdminProtectedRoute><AdminCouponsPage /></AdminProtectedRoute>} />
              <Route path="/mgmt-a8f4c2d9e1b7/pricing" element={<AdminProtectedRoute><AdminPricingPage /></AdminProtectedRoute>} />
              <Route path="/mgmt-a8f4c2d9e1b7/automation-settings" element={<AdminProtectedRoute><AdminAutomationSettingsPage /></AdminProtectedRoute>} />
              <Route path="/mgmt-a8f4c2d9e1b7/blog" element={<AdminProtectedRoute><AdminBlogPage /></AdminProtectedRoute>} />
              <Route path="/mgmt-a8f4c2d9e1b7/forum" element={<AdminProtectedRoute><AdminForumPage /></AdminProtectedRoute>} />
              <Route path="/mgmt-a8f4c2d9e1b7/management" element={<AdminProtectedRoute><AdminManagementPage /></AdminProtectedRoute>} />
              <Route path="/mgmt-a8f4c2d9e1b7/demo-chatbot" element={<AdminProtectedRoute><AdminDemoChatbotPage /></AdminProtectedRoute>} />
              <Route path="/mgmt-a8f4c2d9e1b7/partners" element={<AdminProtectedRoute><AdminPartnersPage /></AdminProtectedRoute>} />
              <Route path="/mgmt-a8f4c2d9e1b7/directory" element={<AdminProtectedRoute><AdminDirectoryPage /></AdminProtectedRoute>} />
              <Route path="/mgmt-a8f4c2d9e1b7/sales-inbox" element={<AdminProtectedRoute><AdminSalesInboxPage /></AdminProtectedRoute>} />

              {/* ══════════════════════════════════════
                  AGENTS
              ══════════════════════════════════════ */}
              <Route path="/agent/login" element={<AgentLoginPage />} />
              <Route path="/agent" element={<AgentDashboardPage />} />
              <Route path="/agent/chats" element={<AgentChatsPage />} />
              <Route path="/agent/queue" element={<AgentQueuePage />} />
              <Route path="/agent/history" element={<AgentHistoryPage />} />
              <Route path="/agent/settings" element={<AgentSettingsPage />} />
              <Route path="/agent/chat/:handoffId" element={<AgentChatPage />} />

              {/* Obscure old admin URL — bounce visitors to home so the admin panel isn't discoverable */}
              <Route path="/admin" element={<Navigate to="/" replace />} />
              <Route path="/admin/*" element={<Navigate to="/" replace />} />

              {/* Catch-all */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
          <CookieConsentBanner />
        </BrowserRouter>
        <Toaster />
        <SonnerToaster />
      </div>
    </TranslateProvider>
  );
}

export default App;
